import "./KanbanBoard.css";

import { useEffect, useRef } from "react";

import { KanbanBoardProps } from "../../models";
import { KanbanColumn } from "../KanbanColumn";
import { useKanbanBoard } from "./useKanbanBoard";

/**
 * The Kanban view: six fixed lanes, drag-and-drop and multi-select. The page header, the view
 * switcher, the filters and the hide toggles live on `TasksDashboard` — they are shared with the
 * Gantt and List views — so this component receives an already-filtered task list.
 */
export const KanbanBoard = (props: KanbanBoardProps): JSX.Element => {
  const { variant, isLoading = false, onTaskClick, onObjectClick, onLaneShowMore } = props;

  const {
    columns,
    dragState,
    selectedIds,
    resolveDragCount,
    toggleSelection,
    clearSelection,
    onDragStart,
    onDragEnd,
    onColumnDragOver,
    onColumnDragLeave,
    onColumnDrop,
  } = useKanbanBoard(props);

  // Track whether Shift is held (so cards can preview the multi-select ring on hover) and clear
  // the selection on Escape. The Shift flag is a class on the root — no re-render — to stay off
  // the drag/render hot path.
  const boardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = boardRef.current;
    if (!root) return undefined;
    const setShift = (active: boolean): void => {
      root.classList.toggle("KanbanBoard_shiftActive", active);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Shift") setShift(true);
      if (e.key === "Escape") clearSelection();
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.key === "Shift") setShift(false);
    };
    const onBlur = (): void => setShift(false);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [clearSelection]);

  return (
    <div className={`KanbanBoard KanbanBoard_${variant}`} ref={boardRef}>
      {/* Clicking empty board space (not a card) clears the multi-selection — a supplementary
          convenience; selection is also cleared on a plain card click and after a drop. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="KanbanBoard_columns"
        onClick={(e) => {
          if (e.target === e.currentTarget) clearSelection();
        }}
      >
        {columns.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            variant={variant}
            isLoading={isLoading}
            draggingIds={dragState.draggingIds}
            dropIndex={dragState.dropColumn === column.id ? dragState.dropIndex : null}
            placeholderHeight={dragState.height}
            selectedIds={selectedIds}
            onCardDragStart={onDragStart}
            onCardDragEnd={onDragEnd}
            onDragOverColumn={onColumnDragOver}
            onDragLeaveColumn={onColumnDragLeave}
            onDropColumn={onColumnDrop}
            resolveDragCount={resolveDragCount}
            onToggleSelect={toggleSelection}
            onClearSelection={clearSelection}
            onTaskClick={onTaskClick}
            onObjectClick={onObjectClick}
            onShowMore={onLaneShowMore}
          />
        ))}
      </div>
    </div>
  );
};
